import React, { useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause } from '@fortawesome/free-solid-svg-icons';
import Waveform from './Waveform';
import { resolveMediaUrl } from '../utils/taskHelpers';
import { cycleVoiceRate, seekVoiceNote, toggleVoiceNote, useReportVisible, useVoicePlayback } from '../utils/voicePlayback';
import VoiceOriginContext from '../context/VoiceOriginContext';
import '../styles/audio.css';

const fmt = (ms) => {
    const total = Math.round(ms / 1000);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/** Whether the element is on screen - scrolled into view, not merely mounted. */
const useOnScreen = (ref) => {
    const [onScreen, setOnScreen] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { threshold: 0.5 });
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
    return onScreen;
};

/**
 * A posted voice note.
 *
 * Draws the waveform the recorder measured and sent with the file, so a thread
 * of twenty notes renders instantly instead of downloading and decoding twenty
 * audio files to work out what to draw. Nothing is fetched until someone
 * presses play.
 *
 * The playing itself is utils/voicePlayback's, not this bubble's, so a note
 * keeps going when the bubble is unmounted - switching tab, chat or page - and
 * a bubble mounted later picks up where it is. While this bubble is on screen
 * it says so, which keeps the floating player out of the way.
 */
const AudioNote = ({ media, mine, messageId, senderName }) => {
    const url = resolveMediaUrl(media.url);
    const { playing, currentTime, duration, rate } = useVoicePlayback(url);
    const origin = useContext(VoiceOriginContext);
    const rootRef = useRef(null);
    useReportVisible(url, useOnScreen(rootRef));

    const total = media.durationMs || 0;
    const progress = duration ? Math.min(1, currentTime / duration) : 0;

    // Only once the note has been started, as on WhatsApp: an unplayed note has
    // nothing for a speed to apply to yet.
    const started = playing || progress > 0;

    const toggle = () => toggleVoiceNote(url, {
        title: senderName || 'Voice message',
        subtitle: origin?.title || '',
        href: origin?.href || null,
        state: origin?.jumpState && messageId ? origin.jumpState(messageId) : undefined
    });

    return (
        <div ref={rootRef} className={`an ${mine ? 'is-mine' : ''}`}>
            <button type="button" className="an-play" onClick={toggle}
                aria-label={playing ? 'Pause voice note' : 'Play voice note'}>
                <FontAwesomeIcon icon={playing ? faPause : faPlay} />
            </button>

            <Waveform
                peaks={media.waveform}
                progress={progress}
                height={30}
                onSeek={(p) => seekVoiceNote(url, p)}
            />

            {started && (
                <button type="button" className="an-rate" onClick={cycleVoiceRate}
                    aria-label={`Playback speed ${rate}x. Change speed`}>
                    {rate}x
                </button>
            )}

            <span className="an-time">
                {started ? fmt(currentTime * 1000) : fmt(total)}
            </span>
        </div>
    );
};

export default AudioNote;
